// AppAssistantToggle — the magic-star button in AppHeader that flips
// `root.assistantOpen`. The workspace shell's AssistantDrawer (and the
// simone Chat it wraps) reads that flag for its visibility, so the
// toggle is the canonical opener across every workspace surface.
//
// Extracted 2026-06-02 from the legacy shared/components/AppAssistant.js
// so that AppAssistant.js (the heavy drawer body — replaced by
// AssistantDrawer + simone Chat) can be deleted without taking the
// toggle button with it. AppHeader.js mounts AppAssistantToggle by
// bare-key auto-extend, so no consumer changes were required for the
// extraction.
//
// hide rule gates on whether the host registered an `aiStream`
// function. This was originally a guard against showing the toggle in
// apps without a wired AI backend. The new SDK ai service resolves at
// boot via installAppShellSync + simoneAiTransport — both shells that
// mount this toggle (workspace, canvas) also register aiStream — so
// the guard is effectively a no-op in production but kept for the
// dev-time warning + the canvas-standalone case (where aiStream might
// not yet be wired).
import { activeHighlight } from './_activeHighlight.js'
import { headerOverlayPatch } from './headerOverlayState.js'

export const AppAssistantToggle = {
  extends: 'IconButton',
  // FA204 / frank trap — `_toggleAssistantDrawer` lives in `scope`, not
  // module scope: a bare module-scope const silently reads as undefined
  // once the component is frank.toJSON'd for platform delivery (workspace
  // d6ea1142, project_frank_drops_module_local_helpers). Called via
  // `el.scope.toggleAssistantDrawer(...)` from the pointer/keyboard handlers
  // below.
  scope: {
    // Treat "minimized-open" (assistantOpen but collapsed to the pill chip)
    // as NOT fully open, so the star REVEALS the drawer from the chip
    // instead of toggling the assistant off. `!assistantOpen` alone was
    // false in the minimized state, so a press set assistantOpen:false and
    // left nothing visible — the user had to press twice. To DISMISS from
    // minimized, use the drawer's close (✕) after revealing.
    toggleAssistantDrawer: (e, el, s) => {
      const opening = !s.root.assistantOpen || s.root.assistantDock === 'minimized'
      const patch = headerOverlayPatch('assistant', opening)
      // Reveal on open: a minimized dock (persisted or just-minimized)
      // promotes to a visible 'right' dock so the star never opens into an
      // empty pill-only state. (Where `assistantDock` is unset — the
      // standalone extension — no-op.)
      if (opening && s.root.assistantDock === 'minimized') patch.assistantDock = 'right'
      if (opening && s.root.notificationsPanelOpen) el.call('closeDropdown')
      s.root.update(patch)
    }
  },
  title: (el) => el.call('polyglot', 'shell.assistantToggle.tooltip'),
  'aria-label': (el) => el.call('polyglot', 'shell.assistantToggle.ariaLabel'),
  active: (el, s) => !!s.root?.assistantOpen,
  // Open-state highlight on the component (reactive signal read).
  ...activeHighlight,
  class: { isActive: (el, s) => !!s.root?.assistantOpen },
  hide: (el) => {
    const fn = el?.context?.functions?.aiStream
    const missing = typeof fn !== 'function'
    if (missing && typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
      if (!el._aiStreamWarnFired) {
        el._aiStreamWarnFired = true
        console.warn('[AppAssistantToggle] aiStream not registered — toggle hidden.')
      }
    }
    return missing
  },
  // Pointer = onMousedown (fires on the original target before chrome
  // re-renders — worst under PageSlot load churn — can swap the node mid
  // gesture and eat the click); keyboard = detail-0 click. The guard keeps
  // one gesture from double-firing the TOGGLE (open-then-close reads as
  // "the star does nothing"). Same split as HeaderTabs/NotificationBell.
  onMousedown: (e, el, s) => {
    if (e.button !== 0) return
    el.scope.toggleAssistantDrawer(e, el, s)
  },
  onClick: (e, el, s) => {
    if (e.detail !== 0) return
    el.scope.toggleAssistantDrawer(e, el, s)
  },
  // `name` (not `icon`) — required, not stylistic. See Messenger.js's Icon
  // comment for the full mechanism: IconText.Icon.if never reads the
  // child's own `el.icon`, only `el.name` (or `parent.icon`). This toggle
  // rendered empty after smbls `2e4ba032e` removed the SquareButton
  // `icon:'smile'` placeholder that had been masking the wrong key
  // (tickets/opus.md P1, 2026-08-09).
  Icon: { name: 'magicstar', boxSize: 'A A' }
}
